import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useTranslation } from "../context/TranslationContext";
import { useWebSocket } from '../context/WebSocketContext';
import { useNotification } from '../context/NotificationContext';
import { ConnectionEventEmitter } from '../utils/ConnectionEventEmitter';
import { optimizeImage, uploadImages } from "../utils/Images";
import PreviewIcon from './PreviewIcon';
import PopupAsk from './PopupAsk';
import Button from './Button';
import InputGallery from './InputGallery';
import Input from './Input';
import InputAddress from "./InputAddress";
import InputPlace from './InputPlace';
import InputDescription from './InputDescription';
import SelectLanguage from './SelectLanguage'
import Select from './Select';
import Caption from './Caption';
import Text from './Text';
import Gallery from './Gallery';
import Address from './Address';
import Website from './Website';
import config from '../config'; 
import log from "loglevel";
import { validateWebsite } from "../utils/Validation";

import '../styles/List.css'; 

const ListItem = ({ mode, screens, startScreen, editItem=null, onCancel, onFinish, getLabel, getDescription }) => {
  const { t, language } = useTranslation();
  const { sendMessage } = useWebSocket();
  const { showNotification } = useNotification();
  const [screenId, setScreenId] = useState(startScreen);
  const [editScreen, setEditScreen] = useState(false);
  const [buttonStatus, setButtonStatus] = useState('active');
  const [viewLanguage, setViewLanguage] = useState(language);
  const [translateCounter, setTranslateCounter] = useState(0);
  const [progressState, setProgressState] = useState({
    show: false,
    progress: 0,
    total: 100
  });
  const [questionState, setQuestionState] = useState({
    show: false,
    question: "",
    leftText: "",
    leftAction: null,
    leftType: "",
    rightText: "",
    rightAction: null,
    rightType: ""
  });

  var inputRefs = useRef({});

  const [item, setItem] = useState( (editItem&&editItem.id) ? editItem : {
    id: "spain_alicante",
    state: "",
    name: {
      pl: "Alicante",
      en: "Alicante",
      de: "Alicante",
      es: "Alicante"
    },
    slogan: {
      pl: "Słoneczne miasto na wybrzeżu Costa Blanca",
      en: "A sunny city on the Costa Blanca coast",
      de: "Eine sonnige Stadt an der Costa Blanca",
      es: "Una ciudad soleada en la costa de la Costa Blanca"
    },
    address: {
      lat: 38.3452,
      lng: -0.4810,
      address: "Alicante, Spain",
      city: "Alicante",
      street: "",
      streetNumber: "",
      postalCode: "03001",
      url: "https://www.google.com/maps?q=Alicante,Spain"
    },
    images: [
    ],
    description: {
      pl: "🏰 Alicante to perła Costa Blanca, znana z pięknych plaż 🏖️, błękitnego morza 🌊 i bogatej historii 📜. \n\n🌿 W centrum miasta znajduje się słynna promenada Explanada de España, wyłożona milionami marmurowych płytek, które tworzą falujący wzór. To idealne miejsce na spacer w cieniu palm. \n\n⛰️ Nad miastem góruje Zamek Santa Bárbara, potężna twierdza z IX wieku. Ze szczytu rozpościera się spektakularny widok na miasto i Morze Śródziemne. \n\n🏘️ Warto również odwiedzić urokliwe Barrio de Santa Cruz – dzielnicę pełną kolorowych domów, wąskich uliczek i kwiatów w donicach. To serce starego Alicante!",
      en: "🏰 Alicante is the jewel of Costa Blanca, known for its beautiful beaches 🏖️, blue sea 🌊, and rich history 📜. \n\n🌿 The city center features the famous Explanada de España promenade, paved with millions of marble tiles creating a mesmerizing wave pattern. A perfect spot for a walk under palm trees! \n\n⛰️ Overlooking the city stands Santa Bárbara Castle, a mighty fortress dating back to the 9th century. From the top, you can enjoy a breathtaking panoramic view of the city and the Mediterranean. \n\n🏘️ Don't miss Barrio de Santa Cruz – a charming district with colorful houses, narrow streets, and potted flowers. It's the heart of Alicante's old town!",
      de: "🏰 Alicante ist das Juwel der Costa Blanca, bekannt für seine schönen Strände 🏖️, das blaue Meer 🌊 und seine reiche Geschichte 📜. \n\n🌿 Im Stadtzentrum befindet sich die berühmte Promenade Explanada de España, die mit Millionen von Marmorfliesen gepflastert ist und ein faszinierendes Wellenmuster bildet. Ein perfekter Ort für einen Spaziergang unter Palmen! \n\n⛰️ Über der Stadt thront die Burg Santa Bárbara, eine mächtige Festung aus dem 9. Jahrhundert. Von oben hat man einen atemberaubenden Panoramablick auf die Stadt und das Mittelmeer. \n\n🏘️ Besuchen Sie auch das charmante Barrio de Santa Cruz – ein Viertel mit bunten Häusern, engen Gassen und blühenden Pflanzen. Es ist das Herz der Altstadt von Alicante!",
      es: "🏰 Alicante es la joya de la Costa Blanca, conocida por sus hermosas playas 🏖️, su mar azul 🌊 y su rica historia 📜. \n\n🌿 En el centro de la ciudad se encuentra la famosa Explanada de España, un paseo marítimo pavimentado con millones de baldosas de mármol que crean un hipnótico patrón de olas. ¡Un lugar perfecto para caminar bajo las palmeras! \n\n⛰️ Sobre la ciudad se alza el Castillo de Santa Bárbara, una imponente fortaleza que data del siglo IX. Desde la cima, puedes disfrutar de una vista panorámica impresionante de la ciudad y el Mediterráneo. \n\n🏘️ No te pierdas el Barrio de Santa Cruz, un encantador distrito con casas coloridas, calles estrechas y macetas con flores. ¡Es el corazón del casco antiguo de Alicante!"
    },
    more: {
      pl: "🌆 Alicante to miasto o populacji około 340 000 mieszkańców. Jego klimat śródziemnomorski oznacza ponad 300 słonecznych dni w roku! ☀️🌡️\n\n🔥 Jednym z największych wydarzeń są Hogueras de San Juan – Ogniska Świętojańskie. Podczas festiwalu mieszkańcy budują ogromne figury, które są później palone na ulicach! 🔥🎇\n\n🌊 Niedaleko Alicante znajduje się wyspa Tabarca, jedyny zamieszkany rezerwat morski w Hiszpanii. To raj dla miłośników nurkowania i snorkelingu. 🐠🤿\n\n🍷 Region Alicante słynie z lokalnego wina Fondillón – jednego z najstarszych win w Europie, którego tradycja sięga XVI wieku!",
      en: "🌆 Alicante is home to around 340,000 residents and enjoys a Mediterranean climate with over 300 sunny days a year! ☀️🌡️\n\n🔥 One of its biggest events is Hogueras de San Juan – the Bonfires of Saint John. During this festival, massive sculptures are built and then burned on the streets! 🔥🎇\n\n🌊 Near Alicante lies Tabarca Island, Spain’s only inhabited marine reserve. It's a paradise for snorkeling and diving enthusiasts. 🐠🤿\n\n🍷 The Alicante region is famous for its local Fondillón wine – one of Europe's oldest wines, dating back to the 16th century!",
      de: "🌆 Alicante hat rund 340.000 Einwohner und genießt ein mediterranes Klima mit über 300 Sonnentagen pro Jahr! ☀️🌡️\n\n🔥 Eines der größten Feste ist Hogueras de San Juan – die Johannisfeuer. Während dieses Festivals werden riesige Figuren gebaut und dann auf den Straßen verbrannt! 🔥🎇\n\n🌊 In der Nähe von Alicante liegt die Insel Tabarca, das einzige bewohnte Meeresschutzgebiet Spaniens. Ein Paradies für Schnorchler und Taucher! 🐠🤿\n\n🍷 Die Region Alicante ist bekannt für ihren lokalen Fondillón-Wein – einer der ältesten Weine Europas mit einer Tradition aus dem 16. Jahrhundert!",
      es: "🌆 Alicante cuenta con unos 340.000 habitantes y disfruta de un clima mediterráneo con más de 300 días soleados al año. ☀️🌡️\n\n🔥 Uno de los eventos más importantes es Hogueras de San Juan – las Hogueras de San Juan. Durante este festival, se construyen enormes figuras que luego se queman en las calles. 🔥🎇\n\n🌊 Cerca de Alicante se encuentra la Isla de Tabarca, la única reserva marina habitada de España. Es un paraíso para los amantes del buceo y el esnórquel. 🐠🤿\n\n🍷 La región de Alicante es famosa por su vino Fondillón, uno de los más antiguos de Europa, con una tradición que se remonta al siglo XVI!"
    },
    website: "https://www.alicante.com"
  });

  const getText = (keyPath) => {
    if(keyPath) {
      return keyPath.split('.').reduce((acc, key) => acc && acc[key], t);
    }
    return "";
  };

  const updateItemField = (name, value) => {
    setItem((prevItem) => {
      const updatedItem = JSON.parse(JSON.stringify(prevItem));
      const keys = name.split('.');
      let current = updatedItem;
      for (let i = 0; i < keys.length - 1; i++) {
        current = current[keys[i]];
      }
      current[keys[keys.length - 1]] = value;
      return updatedItem;
    });
  };

  const refreshFocus = (name) => {
    setTimeout(() => {
      if(inputRefs) {
        if (inputRefs.current[name]) {
          inputRefs.current[name].focus();
        }
      }
    }, 100); 
  };

  const connectionReceived = useCallback(async (receivedMessage) => {
    if(receivedMessage) {
      if(receivedMessage.area==="ai") {
        if(receivedMessage.command==="translate") {
          if(receivedMessage.status==="ok") {
            setTranslateCounter(prev => (prev-1));
            screens[receivedMessage.screenId].fields.some(field => {
              if(field.multilanguage===true) {
                if(item[field.jsonId]&&item[field.jsonId]) {
                  let etag = screens.module+"_"+item.id+"_"+field.id;
                  if(etag===receivedMessage.etag) {
                    updateItemField(field.jsonId,receivedMessage.translations);
                  }
                }
              }
            });  
          } else {
            showNotification(t.ai.translationNotAvailable);
          }        
        } else
        if(receivedMessage.command==="placeName") {
          if(receivedMessage.status==="ok") {
            sendMessage({
              area: 'ai',
              command: 'placeSlogan',
              description: receivedMessage.description,
              screenId: receivedMessage.screenId,
              etag: receivedMessage.etag
            });
          } else {
            setButtonStatus("active");
            showNotification(t.ai.serviceNotAvailable);
          }
        } else
        if(receivedMessage.command==="placeSlogan") {
          if(receivedMessage.status==="ok") {
            sendMessage({
              area: 'ai',
              command: 'placeDescription',
              description: receivedMessage.description,
              screenId: receivedMessage.screenId,
              etag: receivedMessage.etag
            });
          } else {
            setButtonStatus("active");
            showNotification(t.ai.serviceNotAvailable);
          }
        } else
        if(receivedMessage.command==="placeDescription") {
          if(receivedMessage.status==="ok") {
            sendMessage({
              area: 'ai',
              command: 'placeMore',
              description: receivedMessage.description,
              screenId: receivedMessage.screenId,
              etag: receivedMessage.etag
            });
          } else {
            setButtonStatus("active");
            showNotification(t.ai.serviceNotAvailable);
          }
        } else
        if(receivedMessage.command==="placeMore") {
          setButtonStatus("active");
/*
          if(receivedMessage.status==="ok") {
            setItem(receivedMessage.item);
            const nextId = screens[receivedMessage.screenId].nextId;
            if(nextId && nextId.length>0) {
              setScreenId(nextId);
            } else {
              onFinish();
            }
          } else {
            showNotification(t.ai.serviceNotAvailable);
          }*/
        }  
      } else
      if(receivedMessage.area==="list") {
        if(receivedMessage.command==="exists") {
          if(receivedMessage.status==="yes") {
            setButtonStatus("active");
            showNotification(t.host.UniqueIdExists);
          } else {
            setButtonStatus("active");
            const nextId = screens[receivedMessage.screenId].nextId;
            if(nextId && nextId.length>0) {
              setScreenId(nextId);
            } else {
              onFinish();
            }    
          }
        } else
        if(receivedMessage.command==="add") {
          if(receivedMessage.status==="exists") {
            setButtonStatus("active");
            showNotification(t.host.UniqueIdExists);
          } else
          if(receivedMessage.status==="failed") {
            setButtonStatus("active");
            showNotification(t.host.AddFailed);
          } else {
            sendMessage({
              area: 'photos',
              command: 'createURLs',
              images: receivedMessage.item.images,
              startId: 0,
              directory: receivedMessage.item.module,
              name: receivedMessage.item.id
            });
            setProgressState({
              progress: 0,
              total: 100,
              show: true
            });
          } 
        } else
        if(receivedMessage.command==="update") {
          if(receivedMessage.status==="failed") {
            setButtonStatus("active");
            showNotification(t.host.UpdateFailed);
          } else {
            if (receivedMessage.item.images.some(item => typeof item !== 'number')) {
              const numericItems = receivedMessage.item.images.filter(item => typeof item === 'number');
              const maxValue = Math.max(...numericItems);
              let newImages = [];
              let a = 0;
              for (let i = 0; i < receivedMessage.item.images.length; i++) {
                if (typeof receivedMessage.item.images[i] !== 'number') {
                  newImages[a++] = receivedMessage.item.images[i];
                }
              }
              sendMessage({
                area: 'photos',
                command: 'createURLs',
                images: newImages,
                startId: maxValue + 1,
                directory: receivedMessage.item.module,
                name: receivedMessage.item.id
              });
              setProgressState({
                progress: 0,
                total: 100,
                show: true
              });
            } else {
              setButtonStatus("active");
              onFinish();
              showNotification(t.host.UpdateOk);  
            }
          } 
        } else
        if(receivedMessage.command==="remove") {
          if(receivedMessage.status==="ok") {
            showNotification(t.host.DeleteItemOk);
            onCancel();
          } else {
            showNotification(t.host.DeleteItemFailed);
            setQuestionState({show: false});
          }
        }
      } else
      if(receivedMessage.area==="photos" && receivedMessage.command==="createURLs") {
        let length = receivedMessage.normalUrls.length;
        let max = length * 2;

        try {
          await uploadImages(receivedMessage.images, receivedMessage.normalUrls, (current, total) => { 
            setProgressState({
              progress: current,
              total: max,
              show: true
            });
          });

          optimizeImage(receivedMessage.images, config.gallery.icon.maxWidth, config.gallery.icon.maxHeight, config.gallery.icon.maxSize)
          .then(async (optimized) => {
            await uploadImages(optimized, receivedMessage.iconUrls, (current, total) => {
              setProgressState({
                progress: length+current,
                total: max,
                show: current>=length ? false: true
              });
              if(current>=length) {
                setButtonStatus("active");
                onFinish();

                if(mode==="add") {
                  showNotification(t.host.AddOk);
                } else {
                  showNotification(t.host.UpdateOk);
                }
              }
            });
          })
        } catch (error) {
          setProgressState({
            progress: 100,
            total: 100,
            show: false
          });
          showNotification(t.host.AddFailed);
          setButtonStatus("active");
          sendMessage({
            area: "list",
            command: "remove",
            itemId: item.id,
            module: screens.module
          });
        }
      }
    }
  }, []);

  useEffect(() => {    
    ConnectionEventEmitter.on('received', connectionReceived);
    return () => { 
      ConnectionEventEmitter.off('received', connectionReceived);
    }
  }, []);

  useEffect(() => {
    if(screens[screenId].focusAt) {
      refreshFocus(screens[screenId].focusAt);
    }
    validateValues();
  }, [screenId]);


  useEffect(() => {
    validateValues();
  }, [item]);

  const goToScreen = (id) => {
    setEditScreen(true);
    setScreenId(id);
  }

  const handleBack = () => {
    if(buttonStatus==="loading") {
      return;
    }
    
    if(screens[screenId].backId!==""||(screenId!==screens.editSummary&&editScreen===true)) {
      if(editScreen===false) {
        setScreenId(screens[screenId].backId);
      } else {
        setEditScreen(false);
        if(mode==="add") {
          setScreenId(screens.addSummary);
        } else {
          setScreenId(screens.editSummary);
        }
      }
    } else {
      const areEqual = JSON.stringify(item) === JSON.stringify(editItem);
      if(areEqual) {
        onCancel();
      } else {
        setQuestionState({
          show: true,
          question: t.host.ListCancelAsk,
          leftText: t.general.Yes,
          leftAction: onCancel,
          leftType: "normal",
          rightText: t.general.No,
          rightType: "priority",
          rightAction: () => setQuestionState({show: false})
        });
      }
    }
  };

  const handleNext = () => {
    if(buttonStatus==="inactive") {
      return;
    }

    console.log(item);

    const nextId = screens[screenId].nextId;
    const execute = screens[screenId].execute;

    setEditScreen(false);

    if(execute) {
      setButtonStatus("loading");

      if(execute==="checkId") {
        sendMessage({
          area: "list",
          command: "exists",
          screenId: screenId,
          itemId: item.id,
          module: screens.module
        });
      } else
      if(execute==="generate") {
        sendMessage({
          area: 'ai',
          command: 'placeName',
          description: item.aiDescription,
          screenId: screenId,
          etag: screens.module
        });
      } else 
      if(execute==="save") {
        item.module = screens.module;
        sendMessage({
          area: "list",
          command: "add",
          item: item,
          module: screens.module
        });
      } else 
      if(execute==="update") {
        item.module = screens.module;
        sendMessage({
          area: "list",
          command: "update",
          item: item,
          module: screens.module
        });
      }
    } else {
      if(nextId && nextId.length>=0) {
        setScreenId(nextId);
      } else {
        onFinish();
      }
    }
  };

  const handleLanguageChange = (language) => {
    setViewLanguage(language);
    validateValues();
    if(screens[screenId].focusAt) {
      refreshFocus(screens[screenId].focusAt);
    }
  };

  const handleTranslateAI = () => {
    if(screens[screenId].fields) {
      var languageMap = {
        en: "angielski", 
        pl: "polski",
        de: "niemiecki",
        es: "hiszpański"
      };
      var languageName = languageMap[viewLanguage];
      delete languageMap[viewLanguage];

      var counter = translateCounter;

      screens[screenId].fields.some(field => {
        if(field.multilanguage===true) {
          if(item[field.jsonId]&&item[field.jsonId][viewLanguage]) {
            sendMessage({
              area: "ai",
              command: "translate",
              text: item[field.jsonId][viewLanguage],
              language: languageName,
              code: viewLanguage,
              screenId: screenId,
              target: languageMap,
              etag: screens.module+"_"+item.id+"_"+field.id
            });
            counter++;
          }
        }
      });
      setTranslateCounter(counter);
    }
  };

  const handleTextChange = (value,field) => {
    if(buttonStatus!=="loading") {
      if(field.multilanguage===true) {
        updateItemField(field.jsonId+"."+viewLanguage,value);
      } else {
        updateItemField(field.jsonId,value);
      }
    }
  };

  const handleSelectChange = (value,field) => {
    if(buttonStatus!=="loading") {
      updateItemField(field.jsonId,value);
    }
  };

  const handleWebsiteChange = (value,field) => {
    if(buttonStatus!=="loading") {
      updateItemField(field.jsonId,value);
    }
  };

  const handleEnter = (field) => {
    if(field.focusAt) {
      refreshFocus(field.focusAt);
    }
  };

  const handleImageListChange = (value,field) => {
    updateItemField(field.jsonId,value);
  };

  const handleAddressSelect = (data, field) => {
    updateItemField(field.jsonId,data);
  };

  const deleteItem = () => {
    setQuestionState(prevState => ({
      ...prevState,
      leftType: "loadingNormal",
      leftAction: null,
      rightType: "inactive",
      rightAction: null
    }));
    sendMessage({
      area: "list",
      command: "remove",
      itemId: item.id,
      module: screens.module
    });
  };

  const handleDelete = () => {
    setQuestionState({
      show: true,
      question: t.host.DeleteItemAsk,
      leftText: t.general.Yes,
      leftAction: ()  => deleteItem(),
      leftType: "normal",
      rightText: t.general.No,
      rightAction: () => setQuestionState({show: false}),
      rightType: "priority"
    });    
  };

  const fieldValidate = {
    inputText: (field) => {
      if(field.required===false) {
        return true;
      }
      var value;      
      if(field.multilanguage===true) {
        value = item[field.jsonId][viewLanguage];   
      } else {
        value = item[field.jsonId];
      }
      if(value) {
        if(field.pattern) {
          const allowedCharsPattern = new RegExp(field.pattern, 'g');
          if(allowedCharsPattern.test(value)===false) {
            return false;
          }
        }
        if(value.length>=field.minLength&&value.length<=field.maxLength) {
          return true;
        }
      }
      return false;
    },
    inputDescription: (field) => {
      if(field.required===false) {
        return true;
      }
      var value;

      if(field.multilanguage===true) {
        value = item[field.jsonId][viewLanguage];  
      } else {
        value = item[field.jsonId];
      }
      if(value) {
        if(value.length>=field.minLength&&value.length<=field.maxLength) {
          return true;
        }
      }
      return false;
    },
    inputGallery: (field) => {
      if(field.required===0) {
        return true;
      }
      if(item[field.jsonId].length>=field.required) {
        return true
      }
      return false;
    },
    inputAddress: (field) => {
      if(field.required==="") {
        return true;
      }
      if(field.required==="address") {      
        if(item[field.jsonId].address && item[field.jsonId].address.length>0) {
          return true
        }
      }
      if(field.required==="city") {      
        if(item[field.jsonId].city && item[field.jsonId].city.length>0) {
          return true
        }
      }
      if(field.required==="street") {      
        if(item[field.jsonId].street && item[field.jsonId].street.length>0) {
          return true
        }
      }
      return false;
    },
    inputPlace: (field) => {
      if(field.required===false) {
        return true;
      }
      // TODO: check is place selected
      return true;
    },
    inputWebsite: (field) => {
      if(field.required===false) {
        return true;
      }
      var value = item[field.jsonId];
      if(value) {
        if(value.length>=field.minLength&&value.length<=field.maxLength) {
           return validateWebsite(value);
        }
      }
      return false;
    },
    inputSelect: (field) => {
      if(field.required===false) {
        return true;
      }
      return true;
    },
    select: (field) => {
      if(field.required===false) {
        return true;
      }
      var value = item[field.jsonId];
      if(value) {
        return true;
      }
      return false;
    },
    caption: (field) => {
      return true;
    },
    text: (field) => {
      return true;
    },
    gallery: (field) => {
      return true;
    },
    address: (field) => {
      return true;
    },
    website: (field) => {
      return true;
    },
    previewIcon: (field) => {
      return true;
    }
  };

  const validateValues = () => {
    if(screens[screenId].fields) {
      var r = !screens[screenId].fields.some(item => {
        return (fieldValidate[item.fieldType])(item) === false;
      });
      if(r===true) {
        setButtonStatus("active");
      } else {
        setButtonStatus("inactive");
      }
    } else {
      setButtonStatus("active");     
    }
  };

  const fieldComponents = {
    inputText: (field) => (
      <Input 
        key={"input_"+field.id}
        placeholder={getText(field.hint)} 
        onChange={(value) => handleTextChange(value, field)} 
        onEnter={() => handleEnter(field)} 
        value={field.multilanguage===true ? item[field.jsonId][viewLanguage] ? item[field.jsonId][viewLanguage] : "" : item[field.jsonId]}
        minLength={field.minLength} 
        maxLength={field.maxLength} 
        required={field.required}
        disabled={field.jsonId==="id" ? editScreen : false}
        label={getText(field.label)} 
        ref={(el) => (inputRefs.current[field.id] = el)}
      /> 
    ),
    inputDescription: (field) => (
      <InputDescription 
        key={"description_"+field.id}
        placeholder={getText(field.hint)} 
        onChange={(value) => handleTextChange(value, field)} 
        value={field.multilanguage===true ? item[field.jsonId][viewLanguage] ? item[field.jsonId][viewLanguage] : "" : item[field.jsonId]}
        minLength={field.minLength} 
        maxLength={field.maxLength} 
        required={field.required}
        height={field.height}
        label={getText(field.label)} 
        ref={(el) => (inputRefs.current[field.id] = el)}
      /> 
    ),
    inputGallery: (field) => (
      <InputGallery 
        mode={mode}
        module={screens.module}
        name={item.id}
        timestamp={item.timestamp}
        list={item.images} 
        key={"input_gallery_"+field.id} 
        max={field.max} 
        onListChange={(value) => handleImageListChange(value, field)}
        required={field.required}
      />
    ),
    inputWebsite: (field) => (
      <Input 
        key={"input_"+field.id}
        placeholder={getText(field.hint)} 
        onChange={(value) => handleWebsiteChange(value, field)} 
        onEnter={() => handleEnter(field)} 
        value={item[field.jsonId]}
        minLength={field.minLength} 
        maxLength={field.maxLength} 
        required={field.required}
        label={getText(field.label)} 
        ref={(el) => (inputRefs.current[field.id] = el)}
        /> 
    ),
    inputSelect: (field) => (
      <div key={"input_select_"+field.id} />
    ),
    inputAddress: (field) => (
      <InputAddress 
        key={"input_address_"+field.id} 
        value={item[field.jsonId]}
        onChange={(value) => handleAddressSelect(value, field)} 
        placeholder={getText(field.hint)}
        label={getText(field.label)} 
        required={field.required}
        ref={(el) => (inputRefs.current[field.id] = el)}
      />
    ),
    inputPlace: (field) => (
      <InputPlace 
        key={"input_place_"+field.id}
        placeholder={getText(field.hint)} 
        onChange={(value) => handleTextChange(value, field)} 
        label={getText(field.label)} 
        required={field.required}
        ref={(el) => (inputRefs.current[field.id] = el)}
      /> 
    ),
    select: (field) => (
      <Select 
        key={"caption_"+field.id}
        placeholder={getText(field.hint)} 
        label={getText(field.label)} 
        value={item[field.jsonId]?item[field.jsonId]:field.default?field.default:""} 
        options={field.options}
        colors={field.colors}
        onChange={(value) => handleSelectChange(value, field)} 
      /> 
    ),
    caption: (field) => (
      <Caption 
        key={"select_"+field.id}
        text={getText(field.text)} 
        edit={field.edit?() => goToScreen(field.edit) : null}
      /> 
    ),
    text: (field) => (
      <Text 
        key={"text_"+field.id}
        text={field.multilanguage===true ? item[field.jsonId][viewLanguage] ? item[field.jsonId][viewLanguage] : "" : item[field.jsonId]}
        multiline={field.multiline}
      /> 
    ),
    gallery: (field) => (
      <Gallery 
        key={"gallery_"+field.id}
        images={item[field.jsonId]}
        timestamp={item.timestamp}
        mode={mode}
        module={screens.module}
        name={item.id}
      /> 
    ),
    address: (field) => (
      <Address 
        key={"address_"+field.id}
        value={item[field.jsonId]}
      /> 
    ),
    website: (field) => (
      <Website 
        key={"website_"+field.id}
        url={item[field.jsonId]}
      /> 
    )
  };

  const generateScreen = () => {
      if(screens[screenId].fields) {
        return screens[screenId].fields.map((item) => (
            (fieldComponents[item.fieldType])(item)
        ));
     }
  };

  const getImage = (id,type) => {
    let img = undefined;    
    if(mode==="add") {
      if(item.images.length>id) {
        return item.images[id];
      }
    } else 
    if(mode==="edit") {
      img = config.photos[screens.module]+screens.module+"/"+item.id+"/"+type+"_"+id+".jpg?v=" +item.timestamp;
    }
    return img;
  };

  return (
    <div className="list-container">
      <div className={screens[screenId].showIcon===true ? ( screens[screenId].title ? "list-top-add-icon-title" : "list-top-add-icon" ) : "list-top-add"}>
        {screens[screenId].showIcon===true ? 
          <>
            <PreviewIcon
              key={screens.module}
              image={getImage(0,"normal")}
              label={getLabel(item,viewLanguage)}   
              slogan={getDescription(item,viewLanguage)} 
              onDelete={mode==="edit" ? handleDelete : null}
              showEnter={false} 
              showBorder={false}
            />
            <div className='list-top-add-line'></div> 
          </>
          : ""
        }
        {screens[screenId].title ?
          <div className="list-top-add-title">
            <div className="list-top-add-label">{getText(screens[screenId].title)}</div>
            {screens[screenId].showLanguage===true ? 
              translateCounter===0 ? 
                <div className='list-top-add-language'><SelectLanguage language='en' onChange={handleLanguageChange} onTranslateAI={handleTranslateAI}/></div> 
              :
                <div className={"list-loading-dots"}>
                  <span></span>
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
            : 
              ""}
          </div>
          : ""
        }
      </div>

      <div className={screens[screenId].showIcon===true ? ( screens[screenId].title ? "list-center-add-icon-title" : "list-center-add-icon" ) : "list-center-add"}>
        <div className="list-content-add">
          {generateScreen()}
        </div>
      </div>

      {buttonStatus==="loading" && (<div className="list-loading-overlay"></div>)}

      <div className="list-bottom-add">
        <Button type={buttonStatus==="loading" ? "inactive" : "normal"} onClick={handleBack}>{getText(screens[screenId].buttonLeft)}</Button>
        {!editScreen && (<Button type={buttonStatus==="active" ? "priority" : buttonStatus==="loading" ? "loading" : "inactive"} onClick={handleNext} loadingPercent={100*progressState.progress/progressState.total}>{getText(screens[screenId].buttonRight) }</Button>)}
      </div>

      {questionState.show && (
        <PopupAsk state={questionState} />
      )}
    </div>
  );
}

export default ListItem;